import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { Pagination } from 'components/Pagination/Pagination'
import { EmptyList } from 'components/Table/EmptyList'
import { JustSpinner } from 'components/Loading/Loading'
import { Flex } from 'styling/GlobalStyles'
import { useDebounce } from 'hooks/useDebounde'
import { getOperationalSites } from 'features/operationalManagement/operationalManagementSlice'
import { NavBarSearchIcon } from 'assets/icons/NavBarSearchIcon'
import { SiteAccordion } from '../components/SiteAccordion'
import { SearchField, SearchRow } from '../Organogram.styled'
import { ORGANOGRAM_PAGE_LIMIT as SITES_PAGE_LIMIT } from '../constants'

interface SitesTabProps {
  refreshKey?: number
  onSiteUpdated?: () => void
}

export const SitesTab = ({ refreshKey = 0, onSiteUpdated }: SitesTabProps) => {
  const dispatch = useDispatch<AppDispatch>()
  const { sites, isLoadingSites } = useSelector(
    (state: RootState) => state.operationalManagement,
  )
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 400)

  const lastSearchRef = useRef(debouncedSearch)
  const lastRefreshKeyRef = useRef(refreshKey)

  useEffect(() => {
    const searchChanged = lastSearchRef.current !== debouncedSearch
    const refreshed = lastRefreshKeyRef.current !== refreshKey
    lastSearchRef.current = debouncedSearch
    lastRefreshKeyRef.current = refreshKey

    if ((searchChanged || refreshed) && page !== 1) {
      setPage(1)
      return
    }
    const q = debouncedSearch.trim()
    dispatch(
      getOperationalSites({
        page,
        limit: SITES_PAGE_LIMIT,
        ...(q ? { q } : {}),
      }),
    )
  }, [dispatch, page, debouncedSearch, refreshKey])

  return (
    <>
      <SearchRow>
        <SearchField>
          <NavBarSearchIcon />
          <input
            type='text'
            aria-label='Search sites'
            placeholder='Search sites...'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </SearchField>
      </SearchRow>

      {isLoadingSites && sites.data.length === 0 ? (
        <Flex justcenter mt='32'>
          <JustSpinner />
        </Flex>
      ) : sites.data.length === 0 ? (
        <EmptyList title='No sites yet.' />
      ) : (
        <Flex column gap='0.75rem'>
          {sites.data.map((site) => (
            <SiteAccordion key={site.id} site={site} onUpdated={onSiteUpdated} />
          ))}
        </Flex>
      )}

      {sites.totalPages > 1 && (
        <Flex justcenter mt='16'>
          <Pagination
            pageCount={sites.totalPages}
            setPage={setPage}
            forcePage={page - 1}
          />
        </Flex>
      )}
    </>
  )
}
